/**
 * Boundary Logger — channeled diagnostic logging at recurring failure boundaries.
 *
 * Channels: 'api-shape', 'price-unit', 'stream', 'hydration'
 *
 * DEV: console.warn with structured context.
 * PROD: same + sendClientLog (rate-limited, persisted via relay).
 */

import { sendClientLog } from './clientLog';

export type BoundaryChannel = 'api-shape' | 'price-unit' | 'stream' | 'hydration';

export interface BoundaryLogEntry {
  level: 'warn' | 'error';
  channel: BoundaryChannel;
  message: string;
  context?: Record<string, unknown>;
  timestamp: number;
}

const MAX_BOUNDARY_LOG = 30;
const boundaryRing: BoundaryLogEntry[] = [];

function pushEntry(entry: BoundaryLogEntry): void {
  boundaryRing.unshift(entry);
  if (boundaryRing.length > MAX_BOUNDARY_LOG) boundaryRing.pop();
}

export function getBoundaryLog(): readonly BoundaryLogEntry[] {
  return boundaryRing;
}

export function clearBoundaryLog(): void {
  boundaryRing.length = 0;
}

function formatMsg(channel: BoundaryChannel, msg: string): string {
  return `[boundary:${channel}] ${msg}`;
}

export function boundaryWarn(
  channel: BoundaryChannel,
  msg: string,
  ctx?: Record<string, unknown>
): void {
  const formatted = formatMsg(channel, msg);
  console.warn(formatted, ctx ?? '');
  sendClientLog('warn', formatted, { source: `boundary:${channel}`, context: ctx });
  pushEntry({ level: 'warn', channel, message: msg, context: ctx, timestamp: Date.now() });
}

export function boundaryError(
  channel: BoundaryChannel,
  msg: string,
  ctx?: Record<string, unknown>
): void {
  const formatted = formatMsg(channel, msg);
  console.error(formatted, ctx ?? '');
  sendClientLog('error', formatted, { source: `boundary:${channel}`, context: ctx });
  pushEntry({ level: 'error', channel, message: msg, context: ctx, timestamp: Date.now() });
}
